import { inject } from '@angular/core';
import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpInterceptorFn,
    HttpRequest,
    HttpResponse,
} from '@angular/common/http';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { catchError, Observable, Subject, takeUntil, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';

import { ExecutionStatus } from '../../proxy/models/common';
import { StorageService } from '../services/storage.service';
import { AuthService } from '../services/auth.service';
import { AppInitService } from '../services/app-init.service';
import { LoadingService } from '../services/loading.service';

const subject = new Subject<void>();

const logout = (
    storageService: StorageService,
    authService: AuthService,
    router: Router
) => {
    storageService.clearData();
    authService.logout(() => {
        router.navigate(['/login']);
    });
};

const handleError = async (
    error: HttpErrorResponse,
    toast: ToastrService,
    appInitService: AppInitService,
    router: Router
): Promise<Observable<any>> => {
    if (error.status === 401) {
        toast.error(error.message);
        Sentry.captureException(new Error(error.message));
        await router.navigate(['/home']);
    }

    return throwError(() => error);
};

export const httpRequestInterceptor: HttpInterceptorFn = (
    req: HttpRequest<any>,
    next: HttpHandlerFn
): Observable<HttpEvent<any>> => {
    const authService = inject(AuthService);
    const router = inject(Router);
    const storageService = inject(StorageService);
    const loadingService = inject(LoadingService);
    const toast = inject(ToastrService);
    const appInitService = inject(AppInitService);

    return next(req).pipe(
        map((event: HttpEvent<any>) => {
            if (event instanceof HttpResponse) {
                const status = event.body?.result?.status;
                const errors = event.body?.result?.errors;

                if (status === ExecutionStatus.failure) {
                    const errorNumber = errors?.[0]?.errorNumber;
                    const message = errors?.[0]?.message;

                    if (errorNumber === 1004 || errorNumber === 1005) {
                        logout(storageService, authService, router);
                        subject.next(undefined);
                        subject.complete();
                        loadingService.hideLoader();
                    } else if (errorNumber === -1830258777 || errorNumber === 66601) {
                        router.navigate(['/']);
                    }

                    toast.error('An error has occurred. Please try again in a short while', '', {timeOut: 5000});
                    Sentry.captureException(new Error(message));
                } else if (status === ExecutionStatus.warning) {
                    toast.warning(errors?.[0]?.message, '', {timeOut: 5000});
                    Sentry.captureException(new Error(errors?.[0]?.message));
                }
            }
            return event;
        }),
        catchError((error: HttpErrorResponse) => {
            return throwError(() => handleError(error, toast, appInitService, router));
        }),
        takeUntil(subject),
    );
};
